// src/store/preferences/preferencesSlice.ts
import { createSlice, createAction } from "@reduxjs/toolkit";
import { CurrentClientPreferences } from "@/services/AuthService";

// Actions
export const fetchCurrentClientPreferences = createAction<{
  clientId?: string;
  settings?: unknown;
  userGuid?: string;
  defaultOrganization?: string;
} | undefined>(
  "preferences/fetchCurrentClientPreferences"
);
export const fetchCurrentClientPreferencesSuccess = createAction<CurrentClientPreferences>(
  "preferences/fetchCurrentClientPreferencesSuccess"
);
export const fetchCurrentClientPreferencesFailure = createAction<string>(
  "preferences/fetchCurrentClientPreferencesFailure"
);
export const fetchCurrentClient = createAction(
  "preferences/fetchCurrentClient"
);
export const fetchCurrentClientSuccess = createAction<CurrentClientPreferences>(
  "preferences/fetchCurrentClientSuccess"
);
export const fetchCurrentClientFailure = createAction<string>(
  "preferences/fetchCurrentClientFailure"
);

// State interface
interface PreferencesState {
  currentClientPreferences: CurrentClientPreferences | null;
  currentClient: CurrentClientPreferences | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: PreferencesState = {
  currentClientPreferences: null,
  currentClient: null,
  loading: false,
  error: null,
};

// Slice
const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentClientPreferences, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentClientPreferencesSuccess, (state, action) => {
        state.loading = false;
        state.currentClientPreferences = action.payload;
        state.error = null;
      })
      .addCase(fetchCurrentClientPreferencesFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCurrentClient, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentClientSuccess, (state, action) => {
        state.loading = false;
        state.currentClient = action.payload;
        state.error = null;
      })
      .addCase(fetchCurrentClientFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectCurrentClientPreferences = (state: {
  preferences: PreferencesState;
}) => state.preferences.currentClientPreferences;

export const selectPreferencesLoading = (state: {
  preferences: PreferencesState;
}) => state.preferences.loading;

export const selectPreferencesError = (state: {
  preferences: PreferencesState;
}) => state.preferences.error;

export const selectCurrentClient = (state: {
  preferences: PreferencesState;
}) => state.preferences.currentClient;

export default preferencesSlice.reducer;